import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import AccountPopover from '../Header/AccountPopover';
import { Icon } from '@iconify/react';
import { UserRole } from '../../utils/types/types';
import Support from './Support';
import SidebarUploadIcon from '../activeIcons/SidebarUploadIcon';
import SidebarClientsIcon from '../activeIcons/SidebarClientsIcon';
import SidebarAgentsIcon from '../activeIcons/SidebarAgentsIcon';
import { useAuth } from '../../utils/context/useAuthContext';
import { useUtils } from '../../utils/context/useUtilsContext';

const Sidebar = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { openUploadDialog, closeUploadDialog, isUploadFileDialogOpen } =
    useUtils();

  const { user, isLoading, isAuthenticated } = useAuth();
  const userRole = user?.role;

  const toggleOpenUploadFile = () => {
    if (!isUploadFileDialogOpen) {
      openUploadDialog();
    } else {
      closeUploadDialog();
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        right: 0,
        width: '140px',
        height: '100vh',
        backgroundColor: theme.palette.dark.main,
        zIndex: 1000,
        py: '50px',
        gap: '35px',
      }}
    >
      <AccountPopover />
      <img
        alt="Logo"
        style={{ cursor: 'pointer' }}
        onClick={() => {
          navigate('/dashboard');
        }}
        src="/sidebarLogo2.svg"
        width="100px"
      />
      {userRole === UserRole.ADMIN ? (
        <AdminView
          uploadFileOpen={isUploadFileDialogOpen}
          toggleOpenUploadFile={toggleOpenUploadFile}
        />
      ) : userRole === UserRole.AGENT ? (
        <AgentView
          uploadFileOpen={isUploadFileDialogOpen}
          toggleOpenUploadFile={toggleOpenUploadFile}
        />
      ) : (
        <ClientView
          uploadFileOpen={isUploadFileDialogOpen}
          toggleOpenUploadFile={toggleOpenUploadFile}
        />
      )}
    </Box>
  );
};

export default Sidebar;

const AdminView = ({
  uploadFileOpen,
  toggleOpenUploadFile,
}: {
  uploadFileOpen: boolean;
  toggleOpenUploadFile: () => void;
}) => {
  const navigate = useNavigate();

  const location = useLocation();

  const clientsIcon = location.pathname === '/clients';

  const paymentIcon = location.pathname === '/custom-payment';

  const agentsIcon =
    location.pathname === '/agents' ||
    location.pathname === '/agents/list' ||
    location.pathname === '/agents/files' ||
    location.pathname === '/agents/funds';

  const reportsIcon = location.pathname === '/reports';

  const assignmentsIcon = location.pathname === '/assignments';

  const feesIcon = location.pathname === '/fees';

  return (
    <Stack spacing={5}>
      <Tooltip
        title="לקוחות"
        slotProps={{
          tooltip: {
            sx: {
              fontFamily: 'AlmoniDL',
              fontSize: '12px',
              fontWeight: '700',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '66px',
            height: '66px',
            backgroundColor: '#3A3A3A',
            borderRadius: '15px',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#4A4A4A',
            },
          }}
          onClick={() => navigate('/clients')}
        >
          <SidebarClientsIcon active={clientsIcon} />
        </Box>
      </Tooltip>

      <Tooltip
          title="מעקב שיוכים"
          slotProps={{
            tooltip: {
              sx: {
                fontFamily: 'AlmoniDL',
                fontSize: '12px',
                fontWeight: '700',
              },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '66px',
              height: '66px',
              backgroundColor: '#3A3A3A',
              borderRadius: '15px',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#4A4A4A',
              },
            }}
            onClick={() => navigate('/assignments')}
          >
            <Icon
              icon="mdi:badge-account"
              height={36}
              width={36}
              color={assignmentsIcon ? '#C1FE40' : '#FFF'}
            />
          </Box>
        </Tooltip>

      <Tooltip
        title="תשלום"
        slotProps={{
          tooltip: {
            sx: {
              fontFamily: 'AlmoniDL',
              fontSize: '12px',
              fontWeight: '700',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '66px',
            height: '66px',
            backgroundColor: '#3A3A3A',
            borderRadius: '15px',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#4A4A4A',
            },
          }}
          onClick={() => navigate('/custom-payment')}
        >
          <Icon
            icon="ic:outline-payments"
            color={paymentIcon ? '#C1FE40' : '#FFFFFF'}
            height="35px"
          />
        </Box>
      </Tooltip>
      <Tooltip
        title="סוכנים"
        slotProps={{
          tooltip: {
            sx: {
              fontFamily: 'AlmoniDL',
              fontSize: '12px',
              fontWeight: '700',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '66px',
            height: '66px',
            backgroundColor: '#3A3A3A',
            borderRadius: '15px',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#4A4A4A',
            },
          }}
          onClick={() => navigate('/agents/list')}
        >
          <SidebarAgentsIcon active={agentsIcon} />
        </Box>
      </Tooltip>
      <Tooltip
        title="תוכניות תשלום וקופונים"
        slotProps={{
          tooltip: {
            sx: {
              fontFamily: 'AlmoniDL',
              fontSize: '12px',
              fontWeight: '700',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '66px',
            height: '66px',
            backgroundColor: '#3A3A3A',
            borderRadius: '15px',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#4A4A4A',
            },
          }}
          onClick={() => navigate('/fees')}
        >
          <Icon
            icon="mdi:dollar"
            color={feesIcon ? '#C1FE40' : '#FFFFFF'}
            height="35px"
          />
        </Box>
      </Tooltip>
      <Tooltip
        title="כל הקבצים"
        slotProps={{
          tooltip: {
            sx: {
              fontFamily: 'AlmoniDL',
              fontSize: '12px',
              fontWeight: '700',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '66px',
            height: '66px',
            backgroundColor: '#3A3A3A',
            borderRadius: '15px',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#4A4A4A',
            },
          }}
          onClick={() => navigate('/all-files')}
        >
          <Icon
            icon="mdi:file-document-multiple"
            color={reportsIcon ? '#C1FE40' : '#FFFFFF'}
            height="35px"
          />
        </Box>
      </Tooltip>
    </Stack>
  );
};

const ClientView = ({
  uploadFileOpen,
  toggleOpenUploadFile,
}: {
  uploadFileOpen: boolean;
  toggleOpenUploadFile: () => void;
}) => {
  const navigate = useNavigate();

  const location = useLocation();

  const filesIcon = location.pathname === '/dashboard';
  const employeesIcon = location.pathname === '/employees';

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setIsPopoverOpen(false);
      }
    };

    if (isPopoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopoverOpen]);

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPopoverPosition({
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setIsPopoverOpen(true);
  };

  const handleClosePopover = () => {
    setIsPopoverOpen(false);
  };

  return (
    <Stack spacing={4.5}>
      <Stack spacing={6}>
        <Tooltip
          title="רשימת הפקדות"
          slotProps={{
            tooltip: {
              sx: {
                fontFamily: 'AlmoniDL',
                fontSize: '12px',
                fontWeight: '700',
              },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '66px',
              height: '66px',
              backgroundColor: '#3A3A3A',
              borderRadius: '15px',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#4A4A4A',
              },
            }}
            onClick={() => navigate('/dashboard')}
          >
            <Icon
              icon="basil:invoice-solid"
              height={36}
              width={36}
              color={filesIcon ? '#C1FE40' : '#FFF'}
              style={{ transform: 'scale(1, -1)' }}
            />
          </Box>
        </Tooltip>
      </Stack>

      <Stack>
        <Box
          sx={{
            mt: '250px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Tooltip
            title="הזמנת מומחה"
            slotProps={{
              tooltip: {
                sx: {
                  fontFamily: 'AlmoniDL',
                  fontSize: '12px',
                  fontWeight: '700',
                },
              },
            }}
          >
            <Box
              component="img"
              src="/assets/sidebar/light/inactive.svg"
              sx={{
                '&:hover': {
                  content: 'url(/assets/sidebar/light/active.svg)',
                },
                opacity: isPopoverOpen ? 0 : 1,
              }}
              onClick={handleOpenPopover}
            />
          </Tooltip>

          {isPopoverOpen && (
            <Support
              popoverRef={popoverRef}
              handleClosePopover={handleClosePopover}
            />
          )}
        </Box>

        <Tooltip
          title="העלאת קובץ"
          slotProps={{
            tooltip: {
              sx: {
                fontFamily: 'AlmoniDL',
                fontSize: '12px',
                fontWeight: '700',
              },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              mt: '50px',
              justifyContent: 'center',
              alignItems: 'center',
              width: '66px',
              height: '66px',
              backgroundColor: '#3A3A3A',
              borderRadius: '15px',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#4A4A4A',
              },
            }}
            onClick={toggleOpenUploadFile}
          >
            <SidebarUploadIcon active={uploadFileOpen} />
          </Box>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

const AgentView = ({
  uploadFileOpen,
  toggleOpenUploadFile,
}: {
  uploadFileOpen: boolean;
  toggleOpenUploadFile: () => void;
}) => {
  const navigate = useNavigate();

  const location = useLocation();

  const filesIcon = location.pathname === '/dashboard';
  const employeesIcon = location.pathname === '/employees';

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setIsPopoverOpen(false);
      }
    };

    if (isPopoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopoverOpen]);

  return (
    <Stack spacing={4.5}>
      <Stack spacing={6}>
        <Tooltip
          title="רשימת הפקדות"
          slotProps={{
            tooltip: {
              sx: {
                fontFamily: 'AlmoniDL',
                fontSize: '12px',
                fontWeight: '700',
              },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '66px',
              height: '66px',
              backgroundColor: '#3A3A3A',
              borderRadius: '15px',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#4A4A4A',
              },
            }}
            onClick={() => navigate('/dashboard')}
          >
            <Icon
              icon="basil:invoice-solid"
              height={36}
              width={36}
              color={filesIcon ? '#C1FE40' : '#FFF'}
              style={{ transform: 'scale(1, -1)' }}
            />
          </Box>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
